// ✅ ALTERED: FeedbackFormsList UI to match your portal theme (Orange header + blue borders)
// - Outer Paper: borderRadius 2 + blue border + overflow hidden
// - Orange gradient header bar
// - Body BG = #F2F2F2
// - List rows: surface + zebra tint + hover + left orange accent stripe
// - Number chip: orange
// NOTE: logic unchanged (only UI/theme + small layout polish)

import React from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Alert,
  Chip,
  ListItemIcon,
  Divider,
} from "@mui/material";
import { API_URL } from "./config";
import { useNavigate } from "react-router-dom";

export default function FeedbackFormsList() {
  // ✅ Theme constants (same as your Builder/Fill pages)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const SURFACE_BORDER = "rgba(25,118,210,0.25)";
  const ROW_ALT = "rgba(25,118,210,0.06)";

  const [forms, setForms] = React.useState([]);
  const [err, setErr] = React.useState("");
  const nav = useNavigate();

  React.useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("auth");
        if (!token) {
          setErr("Session expired. Please login again.");
          return;
        }

        const resp = await axios.get(`${API_URL}/api/feedback/forms/active`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setForms(resp.data || []);
      } catch (e) {
        setErr(e?.response?.data?.message || "Failed to load forms");
      }
    })();
  }, []);

  // ✅ common sx helpers
  const surfacePaperSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${SURFACE_BORDER}`,
  };

  const rowSx = (i) => ({
    ...surfacePaperSx,
    mb: 1.25,
    py: 0.5,
    px: 1,
    position: "relative",
    overflow: "hidden",
    backgroundColor: i % 2 ? ROW_ALT : "white",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
    "&:hover": { backgroundColor: "rgba(240,120,40,0.04)" },
  });

  return (
    <Box sx={{ p: 2, backgroundColor: BG }}>
      <Paper
        elevation={5}
        sx={{
          mx: "auto",
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid rgba(25,118,210,0.15)",
        }}
      >
        {/* ✅ Header bar */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 800, lineHeight: 1.2 }}>
              Feedback Forms
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Select a form to submit your feedback
            </Typography>
          </Box>
        </Box>

        {/* ✅ Body */}
        <Box sx={{ p: 2, backgroundColor: BG }}>
          {err && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {err}
            </Alert>
          )}

          {!forms.length ? (
            <Paper variant="outlined" sx={{ ...surfacePaperSx, p: 2 }}>
              <Typography sx={{ opacity: 0.75 }}>
                No feedback forms available right now.
              </Typography>
            </Paper>
          ) : (
            <>
              <Paper variant="outlined" sx={{ ...surfacePaperSx, p: 1.5, mb: 2 }}>
                <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                  Available Forms ({forms.length})
                </Typography>
                <Divider sx={{ mt: 1, borderColor: "rgba(25,118,210,0.12)" }} />
              </Paper>

              <List sx={{ p: 0 }}>
                {forms.map((f, idx) => (
                  <ListItemButton
                    key={f.FormID}
                    onClick={() => nav(`/feedback/forms/${f.FormID}`)}
                    sx={rowSx(idx)}
                  >
                    <ListItemIcon sx={{ minWidth: 52, pl: 0.5 }}>
                      <Chip
                        label={idx + 1}
                        size="small"
                        sx={{
                          fontWeight: 900,
                          backgroundColor: THEME_ORANGE,
                          color: "white",
                        }}
                      />
                    </ListItemIcon>

                    <ListItemText
                      primary={
                        <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
                          {f.Title}
                        </Typography>
                      }
                      secondary={
                        <Typography sx={{ opacity: 0.8 }}>
                          {f.FeedbackType} • {f.Category}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
